'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ChevronRight, Coins, Medal, Ticket } from 'lucide-react'
import Skeleton from '@/components/elements/Skeleton'
import EmptyState from '@/components/elements/EmptyState'
import { Button } from '@/components/elements/Button'
import { useAuth } from '@/lib/commerce/hooks'
import { getLoyaltySummaryApi, getLoyaltyTransactionsApi, getLoyaltyVouchersApi } from '@/lib/commerce/services/loyalty-api'
import { navigateUrl } from '@/utils/client-request'
import { classNames, formatImageURL, formatNumber, formatPrice, formatDateTimeNormal, getVendorPrimaryColor, resolveColorHex } from '@/utils/util'
import { TRANSACTION_TYPE_LABELS, VOUCHER_STATUS_LABELS } from '../loyalty/labels'
import ConvertVoucherModal from '../loyalty/ConvertVoucherModal'

const PAGE_SIZE = 20

const VOUCHER_STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  used: 'bg-red-100 text-red-600',
  expired: 'bg-amber-100 text-amber-700',
  canceled: 'bg-zinc-100 text-zinc-500',
}

export default function ProfileLoyalty({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { session, isLoggedIn } = useAuth()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))

  const [summary, setSummary] = useState<any>(null)
  const [vouchers, setVouchers] = useState<any[]>([])
  const [transactions, setTransactions] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const ctx = { vendorId: vendor?.id, session: session || undefined }

  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    Promise.all([
      getLoyaltySummaryApi(ctx),
      getLoyaltyVouchersApi(ctx),
      getLoyaltyTransactionsApi(ctx, 1, PAGE_SIZE),
    ]).then(([summaryRes, vouchersRes, transactionsRes]) => {
      if (cancelled) return
      setSummary(summaryRes?.data || null)
      setVouchers(Array.isArray(vouchersRes?.data) ? vouchersRes.data : [])
      setTransactions(transactionsRes?.data?.transactions || [])
      setTotal(transactionsRes?.data?.total || 0)
    }).catch(() => {}).finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, refreshKey])

  const loadMore = async () => {
    setLoadingMore(true)
    const nextPage = page + 1
    const { data } = await getLoyaltyTransactionsApi(ctx, nextPage, PAGE_SIZE)
    setLoadingMore(false)
    if (!data?.transactions) return
    setTransactions((prev) => [...prev, ...data.transactions])
    setTotal(data.total || 0)
    setPage(nextPage)
  }

  if (isLoading) {
    return (
      <div className="cont py-4 sm:py-8">
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-1">
            <Skeleton className="w-40 h-7" />
            <Skeleton className="w-72 h-5" />
          </div>
          <Skeleton className="w-full h-24 rounded-xl" />
          <div className="flex flex-col gap-2 mt-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="w-full h-14 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="cont py-4 sm:py-8">
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-1">
          <div className="font-semibold text-lg text-foreground">Лояалти оноо</div>
          <div className="text-sm text-muted-foreground">Таны цуглуулсан оноо болон онооны түүх</div>
        </div>

        {summary && (
          <div className="border border-border rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {summary.currentTier?.icon ? (
                <img src={formatImageURL(summary.currentTier.icon)} alt={summary.currentTier.name} className="size-11 rounded-full object-cover border border-border" />
              ) : (
                <div className="size-11 rounded-full bg-muted flex items-center justify-center"><Medal className="size-5 opacity-60" /></div>
              )}
              <div>
                <div className="text-xs text-muted-foreground">Таны түвшин</div>
                <div className="font-semibold">{summary.currentTier?.name || '—'}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Онооны үлдэгдэл</div>
              <div className="text-xl font-bold tabular-nums flex items-center gap-1.5 justify-end"><Coins className="size-4" style={{ color: primary }} />{formatNumber(summary.balance || 0)}</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button size="sm" icon={Ticket} className="text-white!" style={{ background: primary }} onClick={() => setConvertOpen(true)} isActive={(summary.balance || 0) >= (summary.minPointsToConvert || 1)}>Ваучер болгох</Button>
              <a href={navigateUrl('/loyalty', pathname)} className="text-sm font-medium flex items-center gap-0.5 hover:opacity-80" style={{ color: primary }}>
                Хөтөлбөр үзэх
                <ChevronRight className="size-4" />
              </a>
            </div>
          </div>
        )}

        {vouchers.length > 0 && (
          <div className="flex flex-col gap-2 mt-2">
            <div className="text-sm font-semibold text-foreground">Миний ваучерууд</div>
            <div className="flex flex-col gap-2">
              {vouchers.map((voucher: any) => (
                <div key={voucher.id} className={classNames('flex items-center justify-between border border-border rounded-xl px-4 py-3', voucher.status !== 'active' && 'opacity-75')}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Ticket className={classNames('size-5 shrink-0', voucher.status !== 'active' && 'opacity-50')} style={voucher.status === 'active' ? { color: primary } : undefined} />
                    <div className="min-w-0">
                      <div className="font-medium font-mono text-sm truncate">{voucher.code}</div>
                      <div className="text-xs text-muted-foreground">{voucher.expireAt ? `${formatDateTimeNormal(voucher.expireAt)} хүртэл` : 'Хугацаагүй'}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={classNames('font-semibold tabular-nums', voucher.status !== 'active' && 'line-through text-muted-foreground')}>{formatPrice(Number(voucher.remainingAmount ?? voucher.amount) || 0)}</div>
                    <span className={classNames('inline-block text-[11px] font-medium px-1.5 py-0.5 rounded-full mt-0.5', VOUCHER_STATUS_STYLES[voucher.status] || 'bg-zinc-100 text-zinc-500')}>{VOUCHER_STATUS_LABELS[voucher.status] || voucher.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {transactions.length === 0 ? (
          <EmptyState icon={Medal} title="Онооны түүх хоосон байна" description="Худалдан авалт хийж оноо цуглуулж эхлээрэй." />
        ) : (
          <div className="flex flex-col gap-2 mt-2">
            <div className="text-sm font-semibold text-foreground">Онооны түүх</div>
            <div className="flex flex-col gap-3">
              <div className="divide-y divide-border border border-border rounded-xl">
                {transactions.map((transaction: any) => (
                  <div key={transaction.id} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{TRANSACTION_TYPE_LABELS[transaction.type] || transaction.type}</div>
                      <div className="text-xs text-muted-foreground truncate">{formatDateTimeNormal(transaction.createdAt)}{transaction.description ? ` - ${transaction.description}` : ''}</div>
                    </div>
                    <div className={classNames('font-semibold tabular-nums shrink-0 pl-3', transaction.points > 0 ? 'text-green-600' : transaction.points < 0 ? 'text-red-500' : '')}>
                      {transaction.points > 0 ? '+' : ''}{formatNumber(transaction.points)}
                    </div>
                  </div>
                ))}
              </div>
              {transactions.length < total && (
                <Button variant="outline" onClick={loadMore} isLoading={loadingMore} className="self-center">Цааш үзэх</Button>
              )}
            </div>
          </div>
        )}
      </div>

      <ConvertVoucherModal open={convertOpen} setOpen={setConvertOpen} vendor={vendor} program={summary} balance={summary?.balance || 0} onConverted={() => setRefreshKey((key) => key + 1)} />
    </div>
  )
}
